import React, {useState, useMemo, useCallback, useEffect} from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  FlatList,
  AccessibilityInfo,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import CloseIcon from '../../../../../assets/icons/CloseIcon';
import Button from '../../../../ui/Button/Button';
import {buttonSizes, buttonVariants} from '../../../../../constants/global';
import {useScreenTheme} from '../../../../../hooks/useScreenTheme';
import {useDispatch} from 'react-redux';
import {showLoader, hideLoader} from '../../../../../store/loaderSlice';
import {useTranslation} from 'react-i18next';
import {createIndirectActivityModalStyles} from './IndirectActivityModal.styles';
import {
  IndirectActivityResponse,
  IndirectActivityModalProps,
} from '../../HeaderProps';
import Typography from '../../../../../constants/Typography';
import { GlobalErrorModal, LoaderOverlay } from '@shared/components/feedback';
import {useIsZoomed} from '../../../../../hooks/useSiteHeaderHeight';

const IndirectActivityModal: React.FC<IndirectActivityModalProps> = ({
  isVisible,
  onClose,
  onConfirm,
  indirectActivityOptions,
}) => {
  const theme = useScreenTheme('RepairDetailsScreen');
  const isZoomed = useIsZoomed();
  const styles = useMemo(
    () => createIndirectActivityModalStyles(theme, isZoomed),
    [theme, isZoomed],
  );
  const [selectedCard, setSelectedCard] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const isSaveDisabled = selectedCard === 0 || isSubmitting;

  useEffect(() => {
    if (!isVisible) {
      setSelectedCard(0);
      setIsSubmitting(false);
    }
  }, [isVisible]);

  useEffect(() => {
    if (Platform.OS !== 'android') return;
    if (isSubmitting) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
  }, [isSubmitting, dispatch]);

  const renderStatusItem = useCallback(
    ({item, index}: {item: IndirectActivityResponse; index: number}) => {
      return (
        <TouchableOpacity
          testID={`statusCard_${item.repairGroupComponentActionID}`}
          accessible={true}
          accessibilityRole="button"
          accessibilityState={{selected: selectedCard === item?.repairGroupComponentActionID}}
          accessibilityLabel={
            selectedCard === item?.repairGroupComponentActionID
              ? `${item.partName}, ${t('header.accessibility.selectedActivityAnnouncement')}`
              : item.partName
          }
          {...(Platform.OS === 'web'
            ? ({
                 role: 'button',
                'aria-pressed': selectedCard === item?.repairGroupComponentActionID,
                'aria-label': selectedCard === item?.repairGroupComponentActionID
                  ? `${item.partName}, ${t('header.accessibility.selectedActivityAnnouncement')}`
                  : item.partName,
              } as any)
            : {})}
          style={[
            styles.noteLog,
            {marginRight: !isZoomed && index % 2 === 0 ? Typography.gap.gap24 : 0},
            selectedCard === item?.repairGroupComponentActionID && styles.selectedCard,
          ]}
          onPress={() => {
            setSelectedCard(item?.repairGroupComponentActionID);
            if (Platform.OS !== 'web') {
              AccessibilityInfo.announceForAccessibility(
                `${item.partName}, ${t('header.accessibility.selectedActivityAnnouncement')}`,
              );
            }
          }}>
          <Text
            style={styles.noteLogTitleText}
            accessible={false}
            importantForAccessibility="no-hide-descendants">
            {item.partName}
          </Text>
        </TouchableOpacity>
      );
    },
    [selectedCard, styles, isZoomed],
  );

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="fade"
      supportedOrientations={[
        'portrait',
        'landscape',
        'landscape-left',
        'landscape-right',
      ]}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={styles.container}>
        <SafeAreaView style={styles.modal}>
          <View style={styles.header}>
            <Text
              style={styles.title}
              testID="modalTitle"
              accessible={true}
              accessibilityRole="header"
              accessibilityLabel={t('header.SelectIndirectActivity')}
              {...(Platform.OS === 'web' ? ({ tabIndex: 0 } as any) : {})}>
              {t('header.SelectIndirectActivity')}
            </Text>
            <TouchableOpacity
              onPress={onClose}
              testID="closeButton"
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel={t('common.accessibility.modalClose')}
              accessibilityHint={t('common.accessibility.closeIndirectActivityModal')}>
              <CloseIcon color={theme.closeModalIconColor} />
            </TouchableOpacity>
          </View>

          <ScrollView
            keyboardShouldPersistTaps="handled"
            style={isZoomed ? {flexGrow: 0, flexShrink: 0} : undefined}>
            <View style={styles.content}>
              <View style={styles.listContainer}>
                <FlatList
                  data={indirectActivityOptions}
                  renderItem={renderStatusItem}
                  // Single column at extreme zoom (numColumns cannot change on a
                  // mounted FlatList, hence the key).
                  key={isZoomed ? 'one-column' : 'two-columns'}
                  numColumns={isZoomed ? 1 : 2}
                  keyExtractor={item => item.repairGroupComponentActionID?.toString()}
                  scrollEnabled={false}
                  columnWrapperStyle={
                    isZoomed
                      ? undefined
                      : {
                          justifyContent: Typography.contentAlign.spaceBetween,
                          marginBottom: Typography.gap.gap24,
                        }
                  }
                />
              </View>
            </View>
          </ScrollView>

          <View style={styles.footerButtons}>
            <Button
              title={t('header.select')}
              variant={buttonVariants.primary}
              size={buttonSizes?.large}
              onPress={() => {
                setIsSubmitting(true);
                onConfirm(selectedCard);
              }}
              disabled={isSaveDisabled}
              fullWidth={false}
              testID="saveButton"
              // containerStyle={
              //   isSaveDisabled
              //     ? styles.disabledSaveContainerStyle
              //     : styles.saveContainerStyle
              // }
              // customTextStyle={
              //   isSaveDisabled
              //     ? styles.disabledSaveCustomTextStyle
              //     : styles.saveCustomTextStyle
              // }
              accessibilityLabel={t('header.accessibility.selectIndirectActivityButton')}
              accessibilityRole="button"
              accessibilityState={{disabled: isSaveDisabled}}
              accessibilityHint={t('header.accessibility.selectIndirectActivityHint')}
            />
          </View>
          {Platform.OS !== 'android' && <GlobalErrorModal />}
          {Platform.OS !== 'android' && <LoaderOverlay visible={isSubmitting} insideModal />}
        </SafeAreaView>
      </KeyboardAvoidingView>
    </Modal>
  );
};

export default IndirectActivityModal;
